import { Component, Host, AfterViewInit, Optional, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { FormGroup, AbstractControl } from "@angular/forms";
import { EditedCasingTubingRecord, EditedFormationRecord, Formation, CasingTubing, Well, WellStatus, WellType, WellPermittingModifiers, Lease, WellLocation, Elevation, Depths, GISPoint, StratigraphicWellBondingRequirement, UpdateStratigraphicWellBondingRequirementRequest, WellComplianceData } from '../../../../../common/models/well.model';
import { SubHeaderService } from '../../../../../core/services/sub-header.service';
import { OrganizationWrapperComponent } from '../organization-wrapper/organization-wrapper.component';
import { Organization } from '../../../../../common/models/organization.model';
import { SidePanelService } from '../../../../../common/services/side-panel.service';
import { SidePanel } from '../../../../../common/models/side-panel.model';
import { AuthService } from '../../../../../services/auth.service';
import { NoteRelatedItemType } from '../../../../../common/models/note_relatedItemType.enum';
import { WellService } from '../../../../services/well.service';
import { Subscription, Observable, of, forkJoin } from 'rxjs';
import { finalize, filter, switchMap } from 'rxjs/operators';
import { LoaderService } from '../../../../services/loader.service';
import { WellDetailsComponent } from '../../../../../wells/well-details/well-details.component';
import { LunaClaimValues } from '../../../../../models/user-identity.model';
import { DynamicDialogService } from "../../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../../core/models/dialog.model";
import { CanDeactivateComponent } from "../../../../../common/classes/can-deactivate.component";
import { NoteService } from '../../../../../common/services/note.service';
import { NoteType } from '../../../../../common/models/note-type.enum';
import { Note } from '../../../../../common/models/note.model';
import { RetentionPolicy } from "../../../../../common/models/retention-policy.model";



@Component({
    selector: 'organization-well-details',
    templateUrl: './organization-well-details.component.html',
    styleUrls: ['./organization-well-details.component.scss'],
    standalone: false
})
export class OrganizationWellDetailsComponent extends CanDeactivateComponent implements AfterViewInit, OnInit, OnDestroy {
    well: Well;
    organization: Organization;
    dataSubscriptions: Subscription = new Subscription();
    wellTypeDescriptionDomainId: string = null;
    wellDispositionLegacyTrackingDomainId: string = null;
    public lunaClaimValues = LunaClaimValues;
    canEditWellTypeDescription = false;
    canEditWellDispositionLegacyTracking = false;
    @ViewChild(WellDetailsComponent, { static: true }) wellDetails: WellDetailsComponent;

    domesticTypeDescriptions: string[] = ['XR', 'XO', 'XB'];
    wellNotes: Note[] = [];

    get canEditWellDetails(): boolean {
        let value = this.auth.hasSystemPermission(LunaClaimValues.CanEditWellDetails);
        return value != null;
    }

    constructor(
        public _activatedRoute: ActivatedRoute,
        public _subHeaderService: SubHeaderService,
        public _sidePanelService: SidePanelService,
        _authService: AuthService,
        public _wellService: WellService,
        public _loaderService: LoaderService,
        public _router: Router,
        _dialog: DynamicDialogService,
        private _noteSvc: NoteService,
        @Host() @Optional() public _organizationWrapper: OrganizationWrapperComponent
    ) {
		super(_dialog, _authService);
        if (this._organizationWrapper)
            this.organization = this._organizationWrapper.organization;

        this._activatedRoute.data.subscribe((result: { well: Well, organization: Organization }) => {
            if (result.well) {
                this.well = result.well;
                let name: string = (this.well.name != null) ? this.well.name : `API #${this.well.id}`;
                let title = `${name} Well Information`;
                let buttons: any[] = null;

                if (this.auth.User.isInternal) {
                    buttons = [];

                    if (this.well.wellStatus && this.well.wellStatus.type) {
                        switch (this.well.wellStatus.type.type) {
                            case "OG_R":
                                this.wellTypeDescriptionDomainId = "OilAndGasWellTypeDescriptions";
                                break;
                            case "ER_R":
                                this.wellTypeDescriptionDomainId = "EnhancedRecoveryWellTypeDescriptions";
                                break;
                            case "SW_R":
                                this.wellTypeDescriptionDomainId = "DisposalWellTypeDescriptions";
                                break;
                            case "UN_R":
                                this.wellTypeDescriptionDomainId = "UnknownWellTypeDescriptions";
                                break;
                        }
                    }

                    if (this.well.wellStatus && this.well.wellStatus.disposition) {
                        switch (this.well.wellStatus.disposition) {
                            case "DG":
                                this.wellDispositionLegacyTrackingDomainId = "WellSpuddedLegacyTrackingOptions";
                                break;
                            case "DR":
                                this.wellDispositionLegacyTrackingDomainId = "WellDrilledLegacyTrackingOptions";
                                break;
                            case "FR":
                                this.wellDispositionLegacyTrackingDomainId = "FinalRestorationLegacyTrackingOptions";
                                break;
                            case "PA":
                                this.wellDispositionLegacyTrackingDomainId = "PluggedLegacyTrackingOptions";
                                break;
                            case "PR":
                                this.wellDispositionLegacyTrackingDomainId = "ProductionStartedLegacyTrackingOptions";
                                break;
                            case "ORPH":
                                this.wellDispositionLegacyTrackingDomainId = "OrphanLegacyTrackingOptions";
                                break;
                            case "UN":
                                this.wellDispositionLegacyTrackingDomainId = "UnknownLegacyTrackingOptions";
                                break;
                        }
                    }

                    //Only push save and cancel buttons, if they have permissions to edit any of the editable fields
                    if (this.auth.hasSystemPermission(LunaClaimValues.EditWellTypeDescription)
                        || this.auth.hasSystemPermission(LunaClaimValues.EditWellDispositionLegacyTracking) || this.auth.hasSystemPermission(LunaClaimValues.CanEditWellDetails)) {
                        buttons.push({ name: "Save", method: () => this.onSubmit() });
                        buttons.push({ name: "Cancel", method: () => this.returnToOrgPage() });
                    }

                    buttons.push({ name: "Well Notes", method: () => this.showNotes() });
                }
                this._subHeaderService.updateSubHeader(
                    {
                        title: title,
                        buttons: buttons
                    });
			}
			if (result.organization) {
				this.organization = result.organization;
			} else if (!this.organization) {
				this.organization = this._activatedRoute.parent?.snapshot?.data?.['organization'];
			}
        });
    }

    ngOnInit() {
        this.allowDeactivate.next(true);
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
        this._router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.dataSubscriptions.unsubscribe();
        });

        this._noteSvc.noteAdded$.subscribe(note => this.wellNotes.push(note))
    }

    ngOnDestroy() {
        if (this.dataSubscriptions) {
            this.dataSubscriptions.unsubscribe();
        }
    }

    ngAfterViewInit() {
        this._sidePanelService.newSidePanel(new SidePanel({
            tabs: [],
            sections: [
                { componentName: 'WellNotes', componentData: { data: this.well, title: 'Well Notes', noteType: NoteRelatedItemType.Well } },
            ],
            visible: false
        }));

        //Only pass in domainId to populate dropdowns if they have edit permissions
        if (this.auth.hasSystemPermission(LunaClaimValues.EditWellTypeDescription)) {
            this.canEditWellTypeDescription = true;
            this.wellDetails.wellTypeDescriptionDomainId = this.wellTypeDescriptionDomainId;
        }

        if (this.auth.hasSystemPermission(LunaClaimValues.EditWellDispositionLegacyTracking)) {
            this.canEditWellDispositionLegacyTracking = true;
            this.wellDetails.wellDispositionLegacyTrackingDomainId = this.wellDispositionLegacyTrackingDomainId;
        }
    }

    casingRecordUpdated(record: EditedCasingTubingRecord): void {
        this.allowDeactivate.next(false);
    }

    formationRecordUpdated(record: EditedFormationRecord): void {
        this.allowDeactivate.next(false);
    }

    handleFormUpdated(updated: boolean): void {
        this.allowDeactivate.next(!updated);
    }

    onSubmit() {
        let obs$: Observable<any> = of(null);
        const complianceDatesForm = this.wellDetails.complianceDatesEditorForm;

        if (complianceDatesForm != null) {
            if(this.well.wellComplianceData == null){
                this.well.wellComplianceData = {
                    apiNumber: this.well.id
                } as WellComplianceData;
            }
            // Permit Issued
            if (complianceDatesForm.get('permitIssued').dirty) {
                this.well.constructionPermit.dateApproved = complianceDatesForm.get('permitIssued').get('permitIssued').value;
                let commentBox = complianceDatesForm.get('permitIssued').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Permit Expired
            if (complianceDatesForm.get('permitExpired').dirty) {
                this.well.constructionPermit.dateExpired = complianceDatesForm.get('permitExpired').get('permitExpired').value;
                let commentBox = complianceDatesForm.get('permitExpired').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Permit Revoked
            if (complianceDatesForm.get('permitRevoked').dirty) {
                this.well.dateOfRevocation = complianceDatesForm.get('permitRevoked').get('permitRevoked').value;
                let commentBox = complianceDatesForm.get('permitRevoked').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Spud Date
            if (complianceDatesForm.get('spudDate').dirty) {
                this.well.dateWellDrillingStarted = complianceDatesForm.get('spudDate').get('spudDate').value;
                let commentBox = complianceDatesForm.get('spudDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Date Total Depth Reached
            if (complianceDatesForm.get('dateTotalDepthReached').dirty) {
                this.well.dateTotalDepthReached = complianceDatesForm.get('dateTotalDepthReached').get('dateTotalDepthReached').value;
                let commentBox = complianceDatesForm.get('dateTotalDepthReached').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Date Of First Production
            if (complianceDatesForm.get('dateOfFirstProduction').dirty) {
                if (!this.well.production) this.well.production = {} as any;
                this.well.production.dateOfFirstProduction = complianceDatesForm.get('dateOfFirstProduction').get('dateOfFirstProduction').value;
                let commentBox = complianceDatesForm.get('dateOfFirstProduction').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Plug Date
            if (complianceDatesForm.get('plugDate').dirty) {

                this.well.plugging.datePluggedAndAbandoned = complianceDatesForm.get('plugDate').get('plugDate').value;
                let commentBox = complianceDatesForm.get('plugDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Replug Date
            if (complianceDatesForm.get('replugDate').dirty) {

                this.well.plugging.dateOfPlugBack = complianceDatesForm.get('replugDate').get('replugDate').value;
                let commentBox = complianceDatesForm.get('replugDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Final Restoration Date
            if (complianceDatesForm.get('finalRestorationDate').dirty) {
                let status = ["FR"];
                //since this field changes the well status updated date we should only do this when the well status is Final Restoration
                if (status.includes(this.well.wellStatus.disposition) !== false) {

                    this.well.wellStatus.dateLastUpdated = complianceDatesForm.get('finalRestorationDate').get('finalRestorationDate').value;
                    let commentBox = complianceDatesForm.get('finalRestorationDate').get('comments');
                    if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                        let note: Note = {
                            createdBy: this.auth.User.displayName,
                            createdById: this.auth.User.id,
                            createdDate: new Date(),
                            documentType: "Note",
                            relatedItemId: this.well.id,
                            relatedItemType: NoteRelatedItemType.Well,
                            id: "",
                            isDeleted: false,
                            noteId: "",
                            noteUrgency: 0,
                            priority: 3,
                            referenceUri: "",
                            retentionPolicy: {} as RetentionPolicy,
                            type: NoteType.General,
                            notes: [],
                            files: [],
                            text: commentBox.value
                        };
                        this.wellNotes.push(note);
                    }

                }
            }
            // As-Drilled Plat Preliminary Date
            if (complianceDatesForm.get('asDrilledPlatPreliminaryDate').dirty) {
                this.well.wellComplianceData.asDrilledPlatPreliminaryDate = complianceDatesForm.get('asDrilledPlatPreliminaryDate').get('asDrilledPlatPreliminaryDate').value;
                let commentBox = complianceDatesForm.get('asDrilledPlatPreliminaryDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // As-Drilled Plat Final Date
            if (complianceDatesForm.get('asDrilledPlatFinalDate').dirty) {
                this.well.wellComplianceData.asDrilledPlatFinalDate = complianceDatesForm.get('asDrilledPlatFinalDate').get('asDrilledPlatFinalDate').value;
                let commentBox = complianceDatesForm.get('asDrilledPlatFinalDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Borehole Deviation Survey Date
            if (complianceDatesForm.get('boreholeDeviationSurveyDate').dirty) {
                this.well.wellComplianceData.boreholeDeviationSurveyDate = complianceDatesForm.get('boreholeDeviationSurveyDate').get('boreholeDeviationSurveyDate').value;
                let commentBox = complianceDatesForm.get('boreholeDeviationSurveyDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Revised Unit Date
            if (complianceDatesForm.get('revisedUnitDate').dirty) {
                this.well.wellComplianceData.revisedUnitDate = complianceDatesForm.get('revisedUnitDate').get('revisedUnitDate').value;
                let commentBox = complianceDatesForm.get('revisedUnitDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Form 8 Preliminary Received Date
            if (complianceDatesForm.get('form8PreliminaryReceivedDate').dirty) {
                this.well.wellComplianceData.form8PreliminaryReceivedDate = complianceDatesForm.get('form8PreliminaryReceivedDate').get('form8PreliminaryReceivedDate').value;
                let commentBox = complianceDatesForm.get('form8PreliminaryReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Form 8 Final Received Date
            if (complianceDatesForm.get('form8FinalReceivedDate').dirty) {
                this.well.wellComplianceData.form8FinalReceivedDate = complianceDatesForm.get('form8FinalReceivedDate').get('form8FinalReceivedDate').value;
                let commentBox = complianceDatesForm.get('form8FinalReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Logs Received Date
            if (complianceDatesForm.get('logsReceivedDate').dirty) {
                this.well.wellComplianceData.logsReceivedDate = complianceDatesForm.get('logsReceivedDate').get('logsReceivedDate').value;
                let commentBox = complianceDatesForm.get('logsReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Form 8A Surface Hole Received Date
            if (complianceDatesForm.get('form8ASurfaceHoleReceivedDate').dirty) {
                this.well.wellComplianceData.form8ASurfaceHoleReceivedDate = complianceDatesForm.get('form8ASurfaceHoleReceivedDate').get('form8ASurfaceHoleReceivedDate').value;
                let commentBox = complianceDatesForm.get('form8ASurfaceHoleReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Form 8B Well Stimulation Received Date
            if (complianceDatesForm.get('form8BWellStimulationReceivedDate').dirty) {
                this.well.wellComplianceData.form8BWellStimulationReceivedDate = complianceDatesForm.get('form8BWellStimulationReceivedDate').get('form8BWellStimulationReceivedDate').value;
                let commentBox = complianceDatesForm.get('form8BWellStimulationReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Cement Ticket Received Date
            if (complianceDatesForm.get('cementTicketReceivedDate').dirty) {
                this.well.wellComplianceData.cementTicketReceivedDate = complianceDatesForm.get('cementTicketReceivedDate').get('cementTicketReceivedDate').value;
                let commentBox = complianceDatesForm.get('cementTicketReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Cement Log Received Date
            if (complianceDatesForm.get('cementLogReceivedDate').dirty) {
                this.well.wellComplianceData.cementLogReceivedDate = complianceDatesForm.get('cementLogReceivedDate').get('cementLogReceivedDate').value;
                let commentBox = complianceDatesForm.get('cementLogReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // PR Graph Received Date
            if (complianceDatesForm.get('prGraphReceivedDate').dirty) {
                this.well.wellComplianceData.prGraphReceivedDate = complianceDatesForm.get('prGraphReceivedDate').get('prGraphReceivedDate').value;
                let commentBox = complianceDatesForm.get('prGraphReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Stimulation Invoice Received Date
            if (complianceDatesForm.get('stimInvoiceReceivedDate').dirty) {
                this.well.wellComplianceData.stimInvoiceReceivedDate = complianceDatesForm.get('stimInvoiceReceivedDate').get('stimInvoiceReceivedDate').value;
                let commentBox = complianceDatesForm.get('stimInvoiceReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Stimulation Record Received Date
            if (complianceDatesForm.get('stimRecordReceivedDate').dirty) {
                this.well.wellComplianceData.stimRecordReceivedDate = complianceDatesForm.get('stimRecordReceivedDate').get('stimRecordReceivedDate').value;
                let commentBox = complianceDatesForm.get('stimRecordReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }
            // Stimulation Log Received Date
            if (complianceDatesForm.get('stimLogReceivedDate').dirty) {
                this.well.wellComplianceData.stimLogReceivedDate = complianceDatesForm.get('stimLogReceivedDate').get('stimLogReceivedDate').value;
                let commentBox = complianceDatesForm.get('stimLogReceivedDate').get('comments');
                if (commentBox && commentBox.value && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    };
                    this.wellNotes.push(note);
                }
            }

        }

        if (this.wellDetails != null && this.wellDetails.postDrillingConfig.wellDetailsEditing) {
            let editForm: FormGroup = this.wellDetails.editorForm;
            if (editForm.get('disposition').dirty || editForm.get('wellType').dirty) {
                let status: WellStatus = this.well.wellStatus;
                if (status == null) status = {} as WellStatus;
                status.dateLastUpdated = new Date(new Date().toISOString());
                status.disposition = editForm.get('disposition').get('disposition').value;
                status.legacyTracking = editForm.get('disposition').get('legacyTracking').value;
                status.type = {
                    type: editForm.get('wellType').get('wellType').value,
                    description: editForm.get('wellType').get('wellTypeDescription').value

                } as WellType;
                status.permittingModifiers = this.well.wellStatus.permittingModifiers;


                if (this.domesticTypeDescriptions.includes(this.well.wellStatus.type.description) && this.domesticTypeDescriptions.includes(status.type.description) == false) {
                    //changing from Domestic type to Non-Domestic type, turn off all Domestic related flags
                    if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) != 0) status.permittingModifiers -= WellPermittingModifiers.ReleaseOfLease;
                    if ((status.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) != 0) status.permittingModifiers -= WellPermittingModifiers.WithoutFiveAcres;
                    if ((status.permittingModifiers & WellPermittingModifiers.ExemptDomestic) != 0) status.permittingModifiers -= WellPermittingModifiers.ExemptDomestic;
                }

                if (this.domesticTypeDescriptions.includes(status.type.description)) {
                    //domestic type, ensure modifiers includes ExemptDomestic and other appropriate settings
                    if ((status.permittingModifiers & WellPermittingModifiers.ExemptDomestic) == 0) status.permittingModifiers += WellPermittingModifiers.ExemptDomestic;
                    //also, perform checks on what's supposed to be done with Release of Lease, Without 5 Acres and Last Transfer Prior to June 2010; all depends on which type was selected
                    switch (status.type.description) {
                        case "XR": {
                            //If marked Last Transferred prior to 2010, remove it
                            if ((status.permittingModifiers & WellPermittingModifiers.LastTransferPriorToJune2010) != 0) status.permittingModifiers -= WellPermittingModifiers.LastTransferPriorToJune2010;
                            //Release of Lease and Without 5 Acres are conditionally editable, so we'll have to look at the necessary fields to determine the allowed combinations
                            if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) != 0 && ((status.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) != 0)) {
                                //first remove release of lease if also Without 5 acres, just based on current settings
                                status.permittingModifiers -= WellPermittingModifiers.ReleaseOfLease;
                            }
                            let hasRelease = editForm.get('wellType').get('hasReleaseOfLease').value;
                            let has5Acres = editForm.get('wellType').get('has5Acres').value;

                            if (has5Acres == true) {
                                //Removing the Without 5 Acres flag requires there to be a release of lease.
                                if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) != 0 || hasRelease == true) {
                                    if ((status.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) != 0) status.permittingModifiers -= WellPermittingModifiers.WithoutFiveAcres;
                                }
                                if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) == 0) status.permittingModifiers += WellPermittingModifiers.ReleaseOfLease;
                            } else {
                                //can add without 5 acres regardless of release of lease
                                if ((status.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) == 0) status.permittingModifiers += WellPermittingModifiers.WithoutFiveAcres;
                            }

                            if (hasRelease == true) {
                                //Release of Lease can only be changed to true if Without Five Acres is also true (flag is backwards)
                                if ((status.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) != 0 || has5Acres == false) {
                                    if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) == 0) status.permittingModifiers += WellPermittingModifiers.ReleaseOfLease;
                                }
                            } else {
                                //Release of lease can be removed regardless of without 5 acres
                                if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) != 0) status.permittingModifiers -= WellPermittingModifiers.ReleaseOfLease;
                            }

                            break;
                        }
                        case "XO": {
                            //If not marked Last Transferred prior to 2010, they need to be
                            if ((status.permittingModifiers & WellPermittingModifiers.LastTransferPriorToJune2010) == 0) status.permittingModifiers += WellPermittingModifiers.LastTransferPriorToJune2010;
                            break;
                        }
                        case "XB": {
                            //If marked Last Transferred prior to 2010, remove it
                            if ((status.permittingModifiers & WellPermittingModifiers.LastTransferPriorToJune2010) != 0) status.permittingModifiers -= WellPermittingModifiers.LastTransferPriorToJune2010;
                            //XB has to be marked as Without5Acres = false and Release of Lease = true, so flip flags accordingly
                            if ((status.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) != 0) status.permittingModifiers -= WellPermittingModifiers.WithoutFiveAcres;
                            if ((status.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) == 0) status.permittingModifiers += WellPermittingModifiers.ReleaseOfLease;
                            break;
                        }
                    }
                }


                this.well.wellStatus = status;
                if (editForm.get('disposition').dirty) {
                    let commentBox: AbstractControl = editForm.get("disposition").get("comments");
                    if (commentBox != null) {
                        let note: Note = {
                            createdBy: this.auth.User.displayName,
                            createdById: this.auth.User.id,
                            createdDate: new Date(),
                            documentType: "Note",
                            relatedItemId: this.well.id,
                            relatedItemType: NoteRelatedItemType.Well,
                            id: "",
                            isDeleted: false,
                            noteId: "",
                            noteUrgency: 0,
                            priority: 3,
                            referenceUri: "",
                            retentionPolicy: {} as RetentionPolicy,
                            type: NoteType.General,
                            notes: [],
                            files: [],
                            text: commentBox.value
                        } as Note;
                        this.wellNotes.push(note);
                    }
                }
                if (editForm.get('wellType').dirty) {
                    let commentBox: AbstractControl = editForm.get("wellType").get("comments");
                    if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                        let note: Note = {
                            createdBy: this.auth.User.displayName,
                            createdById: this.auth.User.id,
                            createdDate: new Date(),
                            documentType: "Note",
                            relatedItemId: this.well.id,
                            relatedItemType: NoteRelatedItemType.Well,
                            id: "",
                            isDeleted: false,
                            noteId: "",
                            noteUrgency: 0,
                            priority: 3,
                            referenceUri: "",
                            retentionPolicy: {} as RetentionPolicy,
                            type: NoteType.General,
                            notes: [],
                            files: [],
                            text: commentBox.value
                        } as Note;
                        this.wellNotes.push(note);
                    }
                }
            }
            if (editForm.get('lease').dirty) {
                let lease: Lease = this.well.lease;
                if (lease == null) lease = {} as Lease;
                lease.name = editForm.get('lease').get('leaseName').value;
                let commentBox: AbstractControl = editForm.get("lease").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
                this.well.lease = lease;
            }
            if (editForm.get('wellSymbol').dirty) {
                this.well.mapSymbol = editForm.get('wellSymbol').get('wellSymbol').value;
                let commentBox: AbstractControl = editForm.get("wellSymbol").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('acresInDrillingUnit').dirty) {
                this.well.acresInDrillingUnit = <number>editForm.get('acresInDrillingUnit').get('acresInDrillingUnit').value;
                let commentBox: AbstractControl = editForm.get("acresInDrillingUnit").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('rigType').dirty) {
                this.well.rigType = editForm.get('rigType').get('rigType').value;
                let commentBox: AbstractControl = editForm.get("rigType").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('wellName').dirty) {
                this.well.name = editForm.get('wellName').get('wellName').value;
                let commentBox: AbstractControl = editForm.get("wellName").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('wellNumber').dirty) {
                this.well.wellNumber = editForm.get('wellNumber').get('wellNumber').value;
                let commentBox: AbstractControl = editForm.get("wellNumber").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('wellClass').dirty) {
                this.well.odnrCurrentClass = editForm.get('wellClass').get('wellClass').value;
                let commentBox: AbstractControl = editForm.get("wellClass").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('mapSymbol').dirty) {
                this.well.mapSym = editForm.get('mapSymbol').get('mapSymbol').value;
                let commentBox: AbstractControl = editForm.get("mapSymbol").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('groundElevation').dirty) {
                let location: WellLocation = this.well.location;
                if (location == null) location = {} as WellLocation;
                if (location.elevation == null) location.elevation = {} as Elevation;
                location.elevation.groundLevel = <number>editForm.get('groundElevation').get('groundElevation').value;
                this.well.location = location;
                let commentBox: AbstractControl = editForm.get("groundElevation").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('dtd').dirty) {
                let depths: Depths = this.well.depths;
                if (depths == null) depths = {} as Depths;
                depths.drillersMeasuredVerticalDepth = <number>editForm.get('dtd').get('dtd').value;
                this.well.depths = depths;
                let commentBox: AbstractControl = editForm.get("dtd").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('targetElevation').dirty) {
                let location: WellLocation = this.well.location;
                if (location == null) location = {} as WellLocation;
                if (location.elevation == null) location.elevation = {} as Elevation;
                location.elevation.target = <number>editForm.get('targetElevation').get('targetElevation').value;
                this.well.location = location;
                let commentBox: AbstractControl = editForm.get("targetElevation").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('kickoff').dirty) {
                let depths: Depths = this.well.depths;
                if (depths == null) depths = {} as Depths;
                depths.kickoffTrueVerticalDepth = <number>editForm.get('kickoff').get('kickoff').value;
                this.well.depths = depths;
                let commentBox: AbstractControl = editForm.get("kickoff").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('digitalElevationModel').dirty) {
                let location: WellLocation = this.well.location;
                if (location == null) location = {} as WellLocation;
                if (location.elevation == null) location.elevation = {} as Elevation;
                location.elevation.digitalElevationModel = <number>editForm.get('digitalElevationModel').get('digitalElevationModel').value;
                this.well.location = location;
                let commentBox: AbstractControl = editForm.get("digitalElevationModel").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('plugbackDepth').dirty) {
                let depths: Depths = this.well.depths;
                if (depths == null) depths = {} as Depths;
                depths.plugbackMeasuredDepth = <number>editForm.get('plugbackDepth').get('plugbackDepth').value;
                this.well.depths = depths;
                let commentBox: AbstractControl = editForm.get("plugbackDepth").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('loggersDepth').dirty) {
                let depths: Depths = this.well.depths;
                if (depths == null) depths = {} as Depths;
                depths.loggerMeasuredVerticalDepth = <number>editForm.get('loggersDepth').get('loggersDepth').value;
                this.well.depths = depths;
                let commentBox: AbstractControl = editForm.get("loggersDepth").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('trueVerticalDepth').dirty) {
                let depths: Depths = this.well.depths;
                if (depths == null) depths = {} as Depths;
                depths.trueVerticalDepth = <number>editForm.get('trueVerticalDepth').get('trueVerticalDepth').value;
                this.well.depths = depths;
                let commentBox: AbstractControl = editForm.get("trueVerticalDepth").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('proposedFormation').dirty) {
                this.well.proposedFormation = editForm.get('proposedFormation').get('proposedFormation').value;
                let commentBox: AbstractControl = editForm.get("proposedFormation").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('dateIssued').dirty) {
                this.well.constructionPermit.dateApproved = editForm.get('dateIssued').get('dateIssued').value;
                let commentBox: AbstractControl = editForm.get("dateIssued").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('spudDate').dirty) {
                this.well.dateWellDrillingStarted = editForm.get('spudDate').get('spudDate').value;
                let commentBox: AbstractControl = editForm.get("spudDate").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('dateOfFirstProduction').dirty) {
                this.well.production.dateOfFirstProduction = editForm.get('dateOfFirstProduction').get('dateOfFirstProduction').value;
                let commentBox: AbstractControl = editForm.get("dateOfFirstProduction").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('dateTotalDepthReached').dirty) {
                this.well.dateTotalDepthReached = editForm.get('dateTotalDepthReached').get('dateTotalDepthReached').value;
                let commentBox: AbstractControl = editForm.get("dateTotalDepthReached").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('datePluggingApproved').dirty) {
                this.well.plugging.datePluggingPlanApproved = editForm.get('datePluggingApproved').get('datePluggingApproved').value;
                let commentBox: AbstractControl = editForm.get("datePluggingApproved").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('datePluggedAndAbandoned').dirty) {
                this.well.plugging.datePluggedAndAbandoned = editForm.get('datePluggedAndAbandoned').get('datePluggedAndAbandoned').value;
                let commentBox: AbstractControl = editForm.get("datePluggedAndAbandoned").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('dateOfPlugBack').dirty) {
                this.well.plugging.dateOfPlugBack = editForm.get('dateOfPlugBack').get('dateOfPlugBack').value;
                let commentBox: AbstractControl = editForm.get("dateOfPlugBack").get("comments");
                if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
            }
            if (editForm.get('location').dirty) {
                this.well.location.sourceOfLocation = editForm.get('location').get('location').value;
                let commentBox: AbstractControl = editForm.get('location').get("comments");
                if (commentBox != null) {
                    let note: Note = {
                        createdBy: this.auth.User.displayName,
                        createdById: this.auth.User.id,
                        createdDate: new Date(),
                        documentType: "Note",
                        relatedItemId: this.well.id,
                        relatedItemType: NoteRelatedItemType.Well,
                        id: "",
                        isDeleted: false,
                        noteId: "",
                        noteUrgency: 0,
                        priority: 3,
                        referenceUri: "",
                        retentionPolicy: {} as RetentionPolicy,
                        type: NoteType.General,
                        notes: [],
                        files: [],
                        text: commentBox.value
                    } as Note;
                    this.wellNotes.push(note);
                }
			}
			if (editForm.get('vaultedVented').dirty) {
				let value = editForm.get('vaultedVented').get('vaultedVented').value;
				if (value == null) {
					this.well.vaultedVented = null;
				} else if (value == 'false' || value == false) {
					this.well.vaultedVented = false;
				} else if (value == 'true' || value == true) {
					this.well.vaultedVented = true;
				}
				let commentBox: AbstractControl = editForm.get('vaultedVented').get("comments");
				if (commentBox != null && commentBox.value != null && (<string>commentBox.value).length > 0) {
					let note: Note = {
						createdBy: this.auth.User.displayName,
						createdById: this.auth.User.id,
						createdDate: new Date(),
						documentType: "Note",
						relatedItemId: this.well.id,
						relatedItemType: NoteRelatedItemType.Well,
						id: "",
						isDeleted: false,
						noteId: "",
						noteUrgency: 0,
						priority: 3,
						referenceUri: "",
						retentionPolicy: {} as RetentionPolicy,
						type: NoteType.General,
						notes: [],
						files: [],
						text: commentBox.value
					} as Note;
					this.wellNotes.push(note);
				}
			}	
            if (this.wellDetails.validPointTypes != null) {
                this.wellDetails.validPointTypes.forEach(t => {
                    let pointForm = editForm.get(t);
                    if (pointForm.dirty) {
                        let id = pointForm.get('identifier').value;
                        if (id != null) { //point should exist
                            let match = this.well.location.gisPoints.find(p => p.identifier == id);
                            if (match != null) {
                                if (match.county != null) {
                                    match.county.apiNumber = pointForm.get('county').get('apiNumber').value;
                                    match.county.name = pointForm.get('county').get('name').value;
                                } else {
                                    match.county = {
                                        apiNumber: pointForm.get('county').get('apiNumber').value,
                                        name: pointForm.get('county').get('name').value
                                    };
                                }
                                match.lot = pointForm.get('lot').value;
                                match.section = pointForm.get('section').value;
                                if (match.township != null) {
                                    match.township.number = pointForm.get('township').get('number').value;
                                    match.township.direction = pointForm.get('township').get('direction').value;
                                    match.township.name = pointForm.get('township').get('name').value;
                                } else {
                                    match.township = {
                                        number: pointForm.get('township').get('number').value,
                                        direction: pointForm.get('township').get('direction').value,
                                        name: pointForm.get('township').get('name').value
                                    };
                                }
                                if (match.range != null) {
                                    match.range.number = pointForm.get('range').get('number').value;
                                    match.range.direction = pointForm.get('range').get('direction').value;
                                    match.range.name = pointForm.get('range').get('name').value;
                                } else {
                                    match.range = {
                                        number: pointForm.get('range').get('number').value,
                                        direction: pointForm.get('range').get('direction').value,
                                        name: pointForm.get('range').get('name').value
                                    };
                                }
                                match.quarterTownship = pointForm.get('quarterTownship').value != null && pointForm.get('quarterTownship').value.toString().trim().length > 0 ? pointForm.get('quarterTownship').value.toString().trim() : null;
                                match.footageCallsFromSectionLines = pointForm.get("footageCallsFromSectionLines").value;
                                match.allotment = pointForm.get('allotment').value;
                                match.originalSurvey = pointForm.get('originalSurvey').value;
                                match.tract = pointForm.get('tract').value;
                                match.fraction = pointForm.get('fraction').value != null && pointForm.get('fraction').value.toString().trim().length > 0 ? pointForm.get('fraction').value.toString().trim() : null;
                                match.civilTownshipNumber = pointForm.get('civilTownshipNumber').value;
                                if (match.ogsQuadrangle != null) {
                                    match.ogsQuadrangle.code = pointForm.get('ogsQuadrangle').get('code').value;
                                    match.ogsQuadrangle.name = pointForm.get('ogsQuadrangle').get('name').value;
                                } else {
                                    match.ogsQuadrangle = {
                                        code: pointForm.get('ogsQuadrangle').get('code').value,
                                        name: pointForm.get('ogsQuadrangle').get('name').value
                                    };
                                }
                                if (match.coordinates != null) {
                                    match.coordinates.statePlaneZone = pointForm.get('coordinates').get('statePlaneZone').value;
                                    match.coordinates.statePlaneXCoordinate = pointForm.get('coordinates').get('statePlaneXCoordinate').value;
                                    match.coordinates.statePlaneYCoordinate = pointForm.get('coordinates').get('statePlaneYCoordinate').value;
                                    match.coordinates.statePlaneOriginalXCoordinate = pointForm.get('coordinates').get('statePlaneOriginalXCoordinate').value;
                                    match.coordinates.statePlaneOriginalYCoordinate = pointForm.get('coordinates').get('statePlaneOriginalYCoordinate').value;
                                    match.coordinates.latitude = pointForm.get('coordinates').get('latitude').value;
                                    match.coordinates.longitude = pointForm.get('coordinates').get('longitude').value;
                                    match.coordinates.nad83XCoordinate = pointForm.get('coordinates').get('nad83XCoordinate').value;
                                    match.coordinates.nad83YCoordinate = pointForm.get('coordinates').get('nad83YCoordinate').value;
                                    match.coordinates.longitude83 = pointForm.get('coordinates').get('longitude83').value;
                                    match.coordinates.latitude83 = pointForm.get('coordinates').get('latitude83').value;
                                } else {
                                    match.coordinates = {
                                        statePlaneZone: pointForm.get('coordinates').get('statePlaneZone').value,
                                        statePlaneXCoordinate: pointForm.get('coordinates').get('statePlaneXCoordinate').value,
                                        statePlaneYCoordinate: pointForm.get('coordinates').get('statePlaneYCoordinate').value,
                                        statePlaneOriginalXCoordinate: pointForm.get('coordinates').get('statePlaneOriginalXCoordinate').value,
                                        statePlaneOriginalYCoordinate: pointForm.get('coordinates').get('statePlaneOriginalYCoordinate').value,
                                        latitude: pointForm.get('coordinates').get('latitude').value,
                                        longitude: pointForm.get('coordinates').get('longitude').value,
                                        nad83XCoordinate: pointForm.get('coordinates').get('nad83XCoordinate').value,
                                        nad83YCoordinate: pointForm.get('coordinates').get('nad83YCoordinate').value,
                                        longitude83: pointForm.get('coordinates').get('longitude83').value,
                                        latitude83: pointForm.get('coordinates').get('latitude83').value
                                    };
                                }
                                match.bearing = pointForm.get('bearing').value;
                                match.distance = pointForm.get('distance').value != null && pointForm.get('distance').value.toString().trim().length > 0 ? pointForm.get('distance').value.toString().trim() : null;
                                match.cardinalPointSection = pointForm.get('cardinalPointSection').value;
                                match.apiNumber = this.well.id;
                            }
                        } else { //point should need to be made
                            let added = pointForm.get('pointAdded').value;
                            let gisRun = pointForm.get('gisQueryRun').value;
                            if (added == true && gisRun == true) {
                                let point: GISPoint = {
                                    pointType: t,
                                    permitId: null,
                                    apiNumber: pointForm.get('apiNumber').value,
                                    county: {
                                        apiNumber: pointForm.get('county').get('apiNumber').value,
                                        name: pointForm.get('county').get('name').value
                                    },
                                    lot: pointForm.get('lot').value,
                                    section: pointForm.get('section').value,
                                    cardinalPointSection: pointForm.get('cardinalPointSection').value,
                                    township: {
                                        number: pointForm.get('township').get('number').value,
                                        direction: pointForm.get('township').get('direction').value,
                                        name: pointForm.get('township').get('name').value
                                    },
                                    range: {
                                        number: pointForm.get('range').get('number').value,
                                        direction: pointForm.get('range').get('direction').value,
                                        name: pointForm.get('range').get('name').value
                                    },
                                    quarterTownship: pointForm.get('quarterTownship').value != null && pointForm.get('quarterTownship').value.toString().trim().length > 0 ? pointForm.get('quarterTownship').value.toString().trim() : null,
                                    footageCallsFromSectionLines: pointForm.get('footageCallsFromSectionLines').value,
                                    allotment: pointForm.get('allotment').value,
                                    originalSurvey: pointForm.get('originalSurvey').value,
                                    tract: pointForm.get('tract').value,
                                    fraction: pointForm.get('fraction').value != null && pointForm.get('fraction').value.toString().trim().length > 0 ? pointForm.get('fraction').value.toString().trim() : null,
                                    civilTownshipNumber: pointForm.get('civilTownshipNumber').value,
                                    ogsQuadrangle: {
                                        code: pointForm.get('ogsQuadrangle').get('code').value,
                                        name: pointForm.get('ogsQuadrangle').get('name').value
                                    },
                                    coordinates: {
                                        statePlaneZone: pointForm.get('coordinates').get('statePlaneZone').value,
                                        statePlaneXCoordinate: pointForm.get('coordinates').get('statePlaneXCoordinate').value,
                                        statePlaneYCoordinate: pointForm.get('coordinates').get('statePlaneYCoordinate').value,
                                        statePlaneOriginalXCoordinate: pointForm.get('coordinates').get('statePlaneOriginalXCoordinate').value,
                                        statePlaneOriginalYCoordinate: pointForm.get('coordinates').get('statePlaneOriginalYCoordinate').value,
                                        latitude: pointForm.get('coordinates').get('latitude').value,
                                        longitude: pointForm.get('coordinates').get('longitude').value,
                                        nad83XCoordinate: pointForm.get('coordinates').get('nad83XCoordinate').value,
                                        nad83YCoordinate: pointForm.get('coordinates').get('nad83YCoordinate').value,
                                        longitude83: pointForm.get('coordinates').get('longitude83').value,
                                        latitude83: pointForm.get('coordinates').get('latitude83').value
                                    },
                                    bearing: pointForm.get('bearing').value,
                                    distance: pointForm.get('distance').value != null && pointForm.get('distance').value.toString().trim().length > 0 ? pointForm.get('distance').value.toString().trim() : null,
                                };
                                this.well.location.gisPoints.push(point);
                            }
                        }
                        let commentBox: AbstractControl = pointForm.get("comments");
                        if (commentBox != null) {
                            let note: Note = {
                                createdBy: this.auth.User.displayName,
                                createdById: this.auth.User.id,
                                createdDate: new Date(),
                                documentType: "Note",
                                relatedItemId: this.well.id,
                                relatedItemType: NoteRelatedItemType.Well,
                                id: "",
                                isDeleted: false,
                                noteId: "",
                                noteUrgency: 0,
                                priority: 3,
                                referenceUri: "",
                                retentionPolicy: {} as RetentionPolicy,
                                type: NoteType.General,
                                notes: [],
                                files: [],
                                text: commentBox.value
                            } as Note;
                            this.wellNotes.push(note);
                        }
                    }
                });
            }
        }
        this._loaderService.toggleLoader(true);
        if (this.well  != null && this.well.wellComplianceData != null) {
            obs$ = obs$.pipe(
                switchMap(() => this._wellService.saveWellComplianceData(this.well.wellComplianceData))
            );
        }
        if (this.wellDetails != null) {
            if (this.wellDetails.modifiedFormationRecords != null && this.wellDetails.modifiedFormationRecords.length > 0) {
                let records: Formation[] = [];
                this.wellDetails.modifiedFormationRecords.forEach(rec => {
                    let record: Formation = rec.toFormation();
                    if (rec.comments != null) {
                        let note: Note = {
                            createdBy: this.auth.User.displayName,
                            createdById: this.auth.User.id,
                            createdDate: new Date(),
                            documentType: "Note",
                            relatedItemId: this.well.id,
                            relatedItemType: NoteRelatedItemType.Well,
                            id: "",
                            isDeleted: false,
                            noteId: "",
                            noteUrgency: 0,
                            priority: 3,
                            referenceUri: "",
                            retentionPolicy: {} as RetentionPolicy,
                            type: NoteType.General,
                            notes: [],
                            files: [],
                            text: rec.comments
                        } as Note;
                        this.wellNotes.push(note);
                    }
                    records.push(record);
                });
                if (records.length > 0) {
                    obs$ = obs$.pipe(
                        switchMap(() => {
                            return this._wellService.saveFormationData(records);
                        })
                    );
                }
            }
            if (this.wellDetails.modifiedCasingRecords != null && this.wellDetails.modifiedCasingRecords.length > 0) {
                let records: CasingTubing[] = [];
                this.wellDetails.modifiedCasingRecords.forEach(rec => {
                    let record: CasingTubing = rec.toCasingTubing();
                    if (rec.comments != null) {
                        let note: Note = {
                            createdBy: this.auth.User.displayName,
                            createdById: this.auth.User.id,
                            createdDate: new Date(),
                            documentType: "Note",
                            relatedItemId: this.well.id,
                            relatedItemType: NoteRelatedItemType.Well,
                            id: "",
                            isDeleted: false,
                            noteId: "",
                            noteUrgency: 0,
                            priority: 3,
                            referenceUri: "",
                            retentionPolicy: {} as RetentionPolicy,
                            type: NoteType.General,
                            notes: [],
                            files: [],
                            text: rec.comments
                        } as Note;
                        this.wellNotes.push(note);
                    }
                    records.push(record);
                });
                if (records.length > 0) {
                    obs$ = obs$.pipe(
                        switchMap(() => {
                            return this._wellService.saveCasingTubingData(records);
                        })
                    );
                }
            }
        }
        if (this.wellNotes.length > 0) {
            let notes$: Observable<Note>[] = [];
            this.wellNotes.forEach(n => {
                notes$.push(this._noteSvc.postNote(n));
            });
            obs$ = obs$.pipe(switchMap(() => {
                return forkJoin(notes$);
            }));
        }
        if (this.wellDetails.pendingBondingRequirement != null) {
            obs$ = obs$.pipe(
                switchMap(() => {
                    let req: UpdateStratigraphicWellBondingRequirementRequest = {
                        requirements: []
                    } as UpdateStratigraphicWellBondingRequirementRequest;
                    req.requirements = this.wellDetails.well.bondingRequirements;
                    return this._wellService.updateStratigraphicWellBondingRequirements(req);
                })
            )
        }
        var wellSaveSubscription$ = this._wellService.saveWell(this.well)
            .pipe(
                switchMap(() => {
                    return obs$;
                }),
                finalize(() => {
                    this.dataSubscriptions.add(wellSaveSubscription$);
                    this._loaderService.toggleLoader(false);
                })
            ).subscribe(
                next => {
                    this.allowDeactivate.next(true);
                    this.returnToOrgPage();
                },
                error => {
                    this.dialogService.showError();
                    console.error(error);
                    this._loaderService.toggleLoader(false);
                }
            );
    }

    returnToOrgPage() {
        const orgId = this.organization?.id ?? this._activatedRoute.snapshot.parent?.params['organizationId'];
        this._router.navigate(["common", "organization", orgId]);
    }

    showNotes() {
        this._sidePanelService.toggleSidePanel(true);
    }
}
